import { useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Globe, CheckCircle2, Star, Building2 } from "lucide-react";
import EnquiryForm from "@/components/EnquiryForm";
import ExploreMore from "@/components/ExploreMore";
import ReferralWidget from "@/components/ReferralWidget";
import { Helmet } from "react-helmet";
import usePageSEOOverride from "@/hooks/useSEOOverrides";

const contactInfo = [
  {
    icon: MapPin,
    title: "Experience Centre",
    details: [
      "A-611-612, Crystal Plaza Premises,",
      "New Link Road, Andheri West,",
      "Mumbai 400053"
    ],
    link: null,
  },
  {
    icon: Phone,
    title: "Call Us",
    details: [
      { text: "9820596811", href: "tel:+919820596811", flash: true },
      { text: "9820042100", href: "tel:+919820042100" },
    ],
  },
  {
    icon: Mail,
    title: "Email Us",
    details: [
      { text: "hello@ezeefitofficesystems.in", href: "mailto:hello@ezeefitofficesystems.in" },
      { text: "eos@ezeefitofficesystems.in", href: "mailto:eos@ezeefitofficesystems.in" },
      { text: "operations@ezeefitofficesystems.in", href: "mailto:operations@ezeefitofficesystems.in" },
    ],
  },
  {
    icon: Clock,
    title: "Office Hours",
    details: ["10:00 AM - 6:30 PM", "Monday - Saturday"],
    link: null,
  },
  {
    icon: Globe,
    title: "Service Area",
    details: ["Serving Pan India"],
    link: null,
  },
  {
    icon: Building2,
    title: "Business Registration",
    details: ["GSTIN: 27AADCE6117G1ZT", "Legal Entity: Ezeefit Modular Furniture Private Limited"],
    link: null,
  },
];

export default function ContactPage() {
  usePageSEOOverride("/contact", {
    title: "Contact Ezeefit Office Systems | Office Furniture Manufacturer Mumbai | Corporate Furniture Solutions",
    description: "Contact Ezeefit for modular office furniture quotes. Call 9820596811 or 9820042100, or email hello@ezeefitofficesystems.in / eos@ezeefitofficesystems.in. Mumbai, Thane, pan-India — factory-direct.",
    canonical: "https://ezeefitofficesystems.in/contact",
  });

  return (
    <div className="pt-20" data-testid="contact-page">
      <Helmet>
        <meta name="description" content="Contact Ezeefit for modular office furniture quotes. Call 9820596811 or 9820042100, or email hello@ezeefitofficesystems.in / eos@ezeefitofficesystems.in. Mumbai, Thane, pan-India — factory-direct." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/contact" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({"@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{"@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in"}, {"@type": "ListItem", "position": 2, "name": "Contact", "item": "https://ezeefitofficesystems.in/contact"}]}) }} />
      {/* Hero */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Animated brand mark — autoplays, muted, loops. Same treatment as About page. */}
            <div
              className="mx-auto mb-8 relative overflow-hidden"
              style={{
                width: "160px",
                height: "160px",
                borderRadius: "50%",
                clipPath: "circle(50% at 50% 50%)",
                boxShadow: "0 20px 45px rgba(0,0,0,0.4)",
              }}
              data-testid="contact-animated-logo-wrapper"
            >
              <video
                src="/media/animated-logo.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                aria-hidden="true"
                data-testid="contact-animated-logo"
              />
            </div>
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Get in Touch
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Request a Workspace Consultation
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto">
              Share your office requirements for furniture, partitions, cabins or complete workspace setup — our experts will guide you with the right solution.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-2xl font-bold text-[#0F172A] mb-8">
                  Contact Information
                </h2>
                <div className="space-y-8">
                  {contactInfo.map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="w-12 h-12 bg-[#F8FAFC] flex items-center justify-center shrink-0">
                        <item.icon className="w-6 h-6 text-[#F97316]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0F172A] mb-1">
                          {item.title}
                        </h3>
                        {item.details.map((detail, i) => {
                          // Support both legacy string entries and structured { text, href, flash } entries.
                          const text = typeof detail === "string" ? detail : detail.text;
                          const href = typeof detail === "string" ? item.link : detail.href || item.link;
                          const flash = typeof detail === "object" && detail.flash;
                          const cls = flash
                            ? "block phone-flash hover:opacity-90 transition-opacity"
                            : "block text-[#64748B] hover:text-[#F97316] transition-colors";
                          return href ? (
                            <a key={i} href={href} className={cls} data-testid={flash ? "contact-primary-phone" : undefined}>
                              {text}
                            </a>
                          ) : (
                            <p key={i} className="text-[#64748B]">{text}</p>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Certifications */}
                <div className="mt-12 pt-8 border-t border-[#E2E8F0]">
                  <h3 className="font-semibold text-[#0F172A] mb-4">
                    Certifications
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    <span className="px-4 py-2 border border-[#E2E8F0] rounded-sm text-sm font-medium text-[#0F172A] bg-[#F8FAFC]">ISO 9001:2015 Certified</span>
                    <span className="px-4 py-2 border border-[#E2E8F0] rounded-sm text-sm font-medium text-[#0F172A] bg-[#F8FAFC]">IGBC Certificate of Compliance</span>
                  </div>
                </div>

                {/* Legal Line */}
                <div className="mt-8 pt-6 border-t border-[#E2E8F0]">
                  <p className="text-sm font-semibold text-[#0F172A]">
                    Ezeefit Modular Furniture Private Limited
                  </p>
                  <p className="text-xs text-[#64748B] mt-1">
                    Operating under the brand "Ezeefit Office Systems"
                  </p>
                  <p className="text-xs text-[#64748B] mt-1">
                    GSTIN: 27AADCE6117G1ZT
                  </p>
                </div>

                {/* Google Map */}
                <div className="mt-8 pt-6 border-t border-[#E2E8F0]">
                  <h3 className="font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#F97316]" />
                    Find Us
                  </h3>
                  <div className="aspect-video w-full border border-[#E2E8F0] rounded-lg overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.2539483267847!2d72.83440507503065!3d19.136023282076893!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b63a0f93c0e9%3A0x7c0b5e9e2a7bca1a!2sCrystal%20Plaza%2C%20New%20Link%20Rd%2C%20Andheri%20West%2C%20Mumbai%2C%20Maharashtra%20400053!5e0!3m2!1sen!2sin!4v1711368000000!5m2!1sen!2sin"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Ezeefit Office Systems Location - Crystal Plaza, Andheri West, Mumbai"
                    ></iframe>
                  </div>
                  <p className="mt-3 text-sm text-[#64748B]">
                    A-611-612, Crystal Plaza Premises, New Link Road, Andheri West, Mumbai 400053
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Enquiry Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-[#F8FAFC] p-8 md:p-12 border border-[#E2E8F0]"
              >
                <h2 className="text-2xl font-bold text-[#0F172A] mb-2">
                  Corporate Project Enquiry
                </h2>
                <p className="text-[#64748B] mb-6">
                  Fill out the form below and our team will contact you within 24 hours.
                </p>
                
                {/* Trust Badges */}
                <div className="flex flex-wrap gap-4 mb-8">
                  <div className="flex items-center gap-2 text-sm text-[#0F172A]">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span>Response within 24 hours</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#0F172A]">
                    <Star className="w-5 h-5 text-[#F97316]" />
                    <span>Free layout consultation</span>
                  </div>
                </div>

                <EnquiryForm />
              </motion.div>

              {/* Why Choose Ezeefit Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mt-8 bg-[#0F172A] p-8 md:p-10 rounded-lg"
              >
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#F97316]"></span>
                  Why Choose Ezeefit?
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    "End-to-End Execution (Furniture + Partitions + Cabins)",
                    "Faster than On-Site Carpentry",
                    "Designed for Scalability & Future Expansion",
                    "ISO 9001:2015 Certified Products",
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 text-white"
                    >
                      <CheckCircle2 className="w-5 h-5 text-[#F97316] shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Referral / Share Widget — brand virality + branded search lift */}
              <ReferralWidget />
            </div>
          </div>
        </div>
      </section>

      <ExploreMore currentPage="contact" />
    </div>
  );
}
